/**
 * Iter 67dh — "Important info & Full rental terms" pop-up.
 *
 * Replaces the existing /vendors/:id/policies page link with a modal
 * that surfaces the most pre-booking-critical facts inline on the car
 * detail page. Matches the screenshot the user provided:
 *
 *  • Header: "Important info" + close ✕
 *  • Section 1 (default open) — INFO BLOCK
 *      - Driver & license requirements (Passport, License, Credit card)
 *      - Security deposit (€) + accepted payment methods
 *      - Damage excess (€) — sourced from the cheapest active insurance plan
 *      - Mileage (Unlimited / X km/day + overage rate)
 *      - Service-provider disclaimer footer
 *  • Section 2 (default collapsed) — FULL RENTAL TERMS
 *      - Renders the vendor's 8 structured sections fetched from
 *        /api/vendors/:id/policies, hiding empty ones.
 *
 * Reuses shadcn `Dialog` for the modal shell and `Accordion` for the
 * two top-level expandables. The block respects the car's existing
 * data shape — no new car-level fields required.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import Money from "@/components/Money";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Info, Check, X, KeyRound, ShieldAlert, Fuel, ShoppingBag, Wrench,
  Gauge, CreditCard, Receipt, IdCard, ScrollText,
} from "lucide-react";

// 8 structured rental-terms keys + their visual treatment. Same palette
// as the vendor-side editor so renters get a consistent look across
// the editor + the public page + this popup.
const TERMS_SECTIONS = [
  { key: "terms_included",            label: "Included in the rental price",        icon: Check,        tone: "text-emerald-700", bg: "bg-emerald-50" },
  { key: "terms_not_included",        label: "Not included in the rental price",    icon: X,            tone: "text-rose-700",    bg: "bg-rose-50" },
  { key: "terms_pickup_requirements", label: "What you need at Pick-up",            icon: KeyRound,     tone: "text-amber-700",   bg: "bg-amber-50" },
  { key: "terms_deposits_excess",     label: "Deposits, excess and cover",          icon: ShieldAlert,  tone: "text-indigo-700",  bg: "bg-indigo-50" },
  { key: "terms_fuel_policy",         label: "Fuel policy",                          icon: Fuel,         tone: "text-sky-700",     bg: "bg-sky-50" },
  { key: "terms_extra_services",      label: "Extra services (payable at counter)",  icon: ShoppingBag,  tone: "text-violet-700",  bg: "bg-violet-50" },
  { key: "terms_extra_equipment",     label: "Extra Equipment",                       icon: Wrench,       tone: "text-teal-700",    bg: "bg-teal-50" },
  { key: "terms_important_info",      label: "Important information",                 icon: Info,         tone: "text-rose-700",    bg: "bg-rose-50" },
];

export default function ImportantInfoPopup({ open, onOpenChange, car }) {
  const [policies, setPolicies] = useState(null);

  // Fetch vendor policies when popup opens — only once per session per car.
  useEffect(() => {
    if (!open || !car?.vendor?.id || policies) return;
    api.get(`/vendors/${car.vendor.id}/policies`)
      .then((r) => setPolicies(r.data))
      .catch(() => setPolicies({ rental_terms: {} }));
  }, [open, car?.vendor?.id, policies]);

  // ── Compute display values from car data ────────────────────────────
  const deposit = Number(car?.deposit || 0);
  // Damage excess — show the worst-case "without extra cover" figure so it
  // matches the wording "Adding the optional Full protection waiver at
  // booking reduces the excess to zero". Prefer the car's baseline
  // `excess`, otherwise the HIGHEST plan excess across active insurance
  // plans (the un-waived worst case). Aligns with /api/cars/<id>/quote
  // which exposes a per-plan excess via the chosen `insurance_plan_id`.
  const planExcesses = (Array.isArray(car?.insurance_plans) ? car.insurance_plans : [])
    .map((p) => Number(p?.excess || 0))
    .filter((v) => v > 0);
  const baselineExcess = Number(car?.excess || 0);
  const damageExcess = baselineExcess > 0
    ? baselineExcess
    : (planExcesses.length > 0 ? Math.max(...planExcesses) : 0);

  const mileagePolicy = car?.mileage_policy;          // "unlimited" | "limited" | null
  const mileageLimit = Number(car?.mileage_limit_km_per_day || 0);
  const mileageExtraRate = Number(car?.mileage_extra_km_rate || 0);

  const acceptedCards = Array.isArray(car?.pickup_payment_methods_resolved)
    ? car.pickup_payment_methods_resolved
    : [];
  // Items may be either plain strings or richer objects like
  // {name, fee_amount, ...} — normalise both shapes.
  const cardsLabel = acceptedCards.length > 0
    ? acceptedCards
        .map((c) => {
          if (typeof c === "string") return c.replace(/_/g, " ");
          if (c && typeof c === "object") return String(c.name || c.label || c.code || "").replace(/_/g, " ");
          return "";
        })
        .filter(Boolean)
        .join(", ")
    : "See vendor terms";

  // Translate the canonical fuel_policy enum into a renter-friendly sentence.
  const FUEL_POLICY_LABEL = {
    full_to_full:   "Pick up FULL, return FULL",
    same_to_same:   "Pick up & return at the SAME fuel level",
    pre_paid:       "Pre-paid full tank — return at any level",
    empty_to_empty: "Pick up & return EMPTY",
  };
  const fuelPolicyRaw = car?.fuel_policy || "full_to_full";
  const fuelPolicy = FUEL_POLICY_LABEL[fuelPolicyRaw] || "See vendor terms for fuel policy details";

  // Always render all 8 sections so the popup structure is consistent across hosts.
  // Sections the vendor hasn't filled yet show a soft placeholder line instead of
  // disappearing — this keeps the renter expectation set and makes it obvious to
  // hosts which panels they still need to author in Policies & legal.
  const allTerms = TERMS_SECTIONS.map((s) => ({
    ...s,
    value: (policies?.rental_terms?.[s.key] || "").trim(),
  }));
  const filledCount = allTerms.filter((s) => s.value.length > 0).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden p-0 gap-0"
        data-testid="important-info-popup"
      >
        <DialogHeader className="px-5 py-4 border-b bg-gradient-to-br from-amber-50 via-white to-amber-50/50">
          <DialogTitle className="flex items-center gap-2 text-lg font-display">
            <span className="h-9 w-9 rounded-xl bg-neutral-900 text-amber-400 flex items-center justify-center shadow-md shadow-neutral-900/20 ring-1 ring-amber-500/30">
              <Info className="h-5 w-5" />
            </span>
            Important info
          </DialogTitle>
          <DialogDescription className="sr-only">
            Pre-booking essentials and the host's full rental terms for this car.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-72px)] px-5 py-4">
          <Accordion type="multiple" defaultValue={["info"]} className="space-y-3">
            {/* ── 1. Always-on Important Info ─────────────────────────── */}
            <AccordionItem value="info" className="border border-border rounded-2xl bg-white px-4">
              <AccordionTrigger className="hover:no-underline py-3" data-testid="important-info-trigger-info">
                <div className="flex items-center gap-3 text-left">
                  <span className="h-9 w-9 rounded-xl bg-indigo-50 ring-1 ring-indigo-100 flex items-center justify-center text-indigo-700">
                    <Info className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-bold text-neutral-900">Important info</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4 pt-1 space-y-5">
                {/* — Driver & license requirements — */}
                <div className="space-y-2" data-testid="important-info-driver-block">
                  <div className="flex items-center gap-2">
                    <IdCard className="h-4 w-4 text-amber-700" />
                    <h4 className="text-sm font-bold text-neutral-900">Driver &amp; license requirements</h4>
                  </div>
                  <p className="text-xs text-neutral-600">When you pick the car up, you'll need:</p>
                  <ul className="space-y-1.5 ml-1">
                    {[
                      ["Passport or national ID card",  "Original document — bring the same one used on your booking."],
                      ["Driving licence",                 "Held for the minimum years required by this vendor."],
                      ["Credit card",                     "In the main driver's name. Used to authorise the deposit."],
                    ].map(([title, sub]) => (
                      <li key={title} className="flex gap-2.5 items-start text-sm">
                        <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                        <div>
                          <div className="font-medium text-neutral-900">{title}</div>
                          <div className="text-[12px] text-neutral-500">{sub}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* — Security deposit — */}
                <div className="rounded-2xl bg-neutral-50 border border-border p-3.5 space-y-1.5" data-testid="important-info-deposit-block">
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4 text-indigo-700" />
                    <h4 className="text-sm font-bold text-neutral-900">
                      Security deposit <Money amount={deposit} className="ml-1 text-indigo-700" />
                    </h4>
                  </div>
                  <p className="text-[12.5px] text-neutral-600 leading-relaxed">
                    At pick-up, the deposit will be pre-authorised on your credit card. It's released
                    back when you return the car undamaged with the same fuel level. Funds are typically
                    visible again within 7–14 working days, depending on your bank.
                  </p>
                  <p className="text-[11px] text-neutral-500 flex items-center gap-1.5 mt-1">
                    <CreditCard className="h-3 w-3" />
                    Accepted cards: <span className="font-semibold text-neutral-700 capitalize">{cardsLabel}</span>
                  </p>
                </div>

                {/* — Damage excess — */}
                <div className="rounded-2xl bg-neutral-50 border border-border p-3.5 space-y-1.5" data-testid="important-info-excess-block">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-rose-700" />
                    <h4 className="text-sm font-bold text-neutral-900">
                      Damage excess <Money amount={damageExcess} className="ml-1 text-rose-700" />
                    </h4>
                  </div>
                  <p className="text-[12.5px] text-neutral-600 leading-relaxed">
                    If the car's bodywork gets damaged during your rental, you'll be charged up to the
                    excess amount above. Adding the optional <em>Full protection</em> waiver at booking
                    reduces the excess to zero — pick it from the insurance plan options at checkout.
                  </p>
                </div>

                {/* — Mileage — */}
                <div className="rounded-2xl bg-neutral-50 border border-border p-3.5 space-y-1.5" data-testid="important-info-mileage-block">
                  <div className="flex items-center gap-2">
                    <Gauge className="h-4 w-4 text-teal-700" />
                    <h4 className="text-sm font-bold text-neutral-900">
                      Mileage {mileagePolicy === "limited"
                        ? <span className="ml-1 text-teal-700">{mileageLimit} km/day</span>
                        : <span className="ml-1 text-teal-700">Unlimited</span>}
                    </h4>
                  </div>
                  {mileagePolicy === "limited" ? (
                    <p className="text-[12.5px] text-neutral-600 leading-relaxed">
                      Your rental includes <strong>{mileageLimit} free km/day</strong>. Beyond that, each
                      extra kilometre is charged at <Money amount={mileageExtraRate} />.
                    </p>
                  ) : (
                    <p className="text-[12.5px] text-neutral-600 leading-relaxed">
                      Your rental includes <strong>unlimited free kilometres</strong> — drive as far as you
                      like across the agreed pickup country (cross-border restrictions may apply, see
                      Important information below).
                    </p>
                  )}
                </div>

                {/* — Fuel policy — */}
                <div className="rounded-2xl bg-neutral-50 border border-border p-3.5 space-y-1.5" data-testid="important-info-fuel-block">
                  <div className="flex items-center gap-2">
                    <Fuel className="h-4 w-4 text-sky-700" />
                    <h4 className="text-sm font-bold text-neutral-900">Fuel policy</h4>
                  </div>
                  <p className="text-[12.5px] text-neutral-600 leading-relaxed">
                    {fuelPolicy}. Missing fuel on return is charged at pump price plus a refuelling fee.
                  </p>
                </div>

                {/* — Disclaimer footer — */}
                <p className="text-[11px] text-neutral-500 leading-relaxed border-t border-dashed pt-3" data-testid="important-info-disclaimer">
                  Conditions and costs may vary depending on the booking. Always read the full rental
                  terms below — they form part of your rental agreement with the Service Provider.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* ── 2. Full rental terms — vendor's 8 structured panels ── */}
            <AccordionItem value="terms" className="border border-border rounded-2xl bg-white px-4">
              <AccordionTrigger
                className="hover:no-underline py-3"
                data-testid="important-info-trigger-terms"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-3 text-left">
                  <span className="h-9 w-9 rounded-xl bg-amber-50 ring-1 ring-amber-100 flex items-center justify-center text-amber-700">
                    <ScrollText className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-bold text-neutral-900">Full rental terms</span>
                  <span className="ml-2 text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">
                    {filledCount} / 8 filled
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4 pt-1">
                {policies === null ? (
                  <div className="text-xs text-neutral-400 py-2">Loading…</div>
                ) : (
                  <Accordion type="multiple" className="space-y-2" data-testid="important-info-terms-list">
                    {allTerms.map(({ key, label, icon: Icon, tone, bg, value }) => (
                      <AccordionItem
                        key={key} value={key}
                        className="rounded-xl border border-border bg-white px-3.5"
                        data-testid={`important-info-term-${key}`}
                      >
                        <AccordionTrigger className="hover:no-underline py-2.5 text-left">
                          <div className="flex items-center gap-2.5 flex-1 min-w-0">
                            <span className={`h-7 w-7 rounded-lg ${bg} flex items-center justify-center ${tone}`}>
                              <Icon className="h-3.5 w-3.5" />
                            </span>
                            <span className="text-sm font-semibold text-neutral-900 truncate">{label}</span>
                            {value.length === 0 && (
                              <span className="ml-auto mr-2 text-[10px] uppercase tracking-wider bg-neutral-100 text-neutral-500 rounded-full px-2 py-0.5">
                                Empty
                              </span>
                            )}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-[13px] leading-relaxed whitespace-pre-wrap pb-3 pt-1 pl-10">
                          {value.length > 0 ? (
                            <span className="text-neutral-700">{value}</span>
                          ) : (
                            <span className="text-neutral-400 italic">
                              The host hasn't filled this section yet — RentalCar.al's platform default
                              applies. Ask the host or check the booking confirmation email for the full text.
                            </span>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </DialogContent>
    </Dialog>
  );
}
